import KeyboardIcon from '@mui/icons-material/Keyboard';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { inventoryLotsByLotIdApi } from 'api/inventory';
import { AddManualLotEnabled } from 'common/utils';
import useHardwareScanner from 'common/hooks/useHardwareScanner';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import AddDrugByLotDialog from './components/AddDrugByLotDialog';
import ConnectionStatusPill from './components/ConnectionStatusPill';
import CurrentSession from './components/CurrentSession';
import ManualRxDialog from './components/ManualRxDialog';
import PendingVialBanner from './components/PendingVialBanner';
import SessionHistory from './components/SessionHistory';
import {
  ScanSessionProvider,
  commitBlockReason,
  resolvePrescribedProductId,
  useScanSession,
} from './contexts/ScanSessionContext';
import { commitSession, wireScanEvent } from './contexts/rxScanValidation';
import useFacilityLots from './hooks/useFacilityLots';
import useScanSocket from './hooks/useScanSocket';
import { playSuccessBeep, playWarningBeep } from './utils/beeps';
import inventoryRowsEqual from './utils/inventoryRowsEqual';
import isVialLotCode, { canonicalVialLot, vialLotKey } from './utils/isVialLotCode';
import ndcsMatch from './utils/ndcsMatch';
import matchScannedLotToInventory, {
  budOf,
  lotRowStatus,
} from './utils/matchScannedLotToInventory';

const normalizeLot = (v) =>
  String(v ?? '')
    .trim()
    .toUpperCase();

const manualScanId = () => `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const RxScanStationInner = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const session = useScanSession();
  const [manualRxOpen, setManualRxOpen] = useState(false);
  const [addDrugOpen, setAddDrugOpen] = useState(false);
  // Lot number / NDC read off a vial the scanner rejected, pre-filled into
  // AddDrugByLotDialog when the operator takes the toast's "Add manually"
  // action. Null when the dialog was opened from its own button.
  const [addDrugSeed, setAddDrugSeed] = useState(null);
  // Only one rejection toast at a time: a second rejected read replaces the
  // first rather than stacking a column of them, each with its own action.
  const unverifiedToastKey = useRef(null);
  // Latest session, for callbacks that resolve after an async lookup and must
  // not act on a session that has since been swapped out.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // The pharmacy a hand-recorded (unverified) lot is claimed against — see
  // AddDrugByLotDialog. The validated Rx record carries it; when it doesn't
  // (older cached records), fall back to the operator's pharmacy, but only
  // when they have exactly one — guessing among several would claim the lot
  // at the wrong site.
  const userPharmacies = useSelector(({ auth }) => auth?.fulfillingPharmacies);
  const userPharmacyIds = (userPharmacies ?? []).map((p) => p.fulfillingPharmacyId);
  const fulfillingPharmacyId =
    session.current?.rx?.fulfillingPharmacyId ??
    (userPharmacyIds.length === 1 ? userPharmacyIds[0] : null);

  // The Rx's product as inventory knows it AT THIS PHARMACY — dispensable stock,
  // depleted lots, NDC list — shared by the vial scan path and
  // AddDrugByLotDialog. See useFacilityLots. Not gated on the manual-lot switch:
  // scoping scans to the facility is a correctness fix (a lot stocked only
  // elsewhere is refused at commit either way); only the unverified-lot route
  // for such a vial is behind the switch.
  const prescribedProductId = resolvePrescribedProductId(session.current?.rx, session.inventory);
  const facility = useFacilityLots({
    enabled: !!session.current,
    productId: prescribedProductId,
    fulfillingPharmacyId,
  });

  const notify = useCallback(
    (msg, variant = 'info') => enqueueSnackbar(msg, { variant }),
    [enqueueSnackbar]
  );

  const openAddDrug = useCallback((seed = null) => {
    setAddDrugSeed(seed);
    setAddDrugOpen(true);
  }, []);

  // The rejection toast for a vial read the scan app could not verify: offers
  // the manual (unverified lot) path pre-filled from the read, instead of a
  // dead-end "re-scan or contact support" — a re-scan of the same vial fails
  // identically, so that advice was never actionable for this case.
  const offerManualEntry = useCallback(
    ({ lotNumber, ndc, reason }) => {
      playWarningBeep();
      const seed = { lotNumber: lotNumber || null, ndc: ndc || null };
      if (unverifiedToastKey.current) closeSnackbar(unverifiedToastKey.current);
      unverifiedToastKey.current = enqueueSnackbar(
        `${reason} — record it by hand as a ${SYSTEM_DEFICIT.toLowerCase()} lot, or re-scan if the read is wrong.`,
        {
          variant: 'warning',
          autoHideDuration: 12000,
          action: (key) => (
            <Button
              size='small'
              color='inherit'
              onClick={() => {
                closeSnackbar(key);
                unverifiedToastKey.current = null;
                openAddDrug(seed);
              }}
            >
              Add manually
            </Button>
          ),
        }
      );
    },
    [enqueueSnackbar, closeSnackbar, openAddDrug]
  );

  // Add a scanned vial whose lot inventory has on record but drained: an
  // UNVERIFIED lot, flagged as such in the row, carrying the scan's own id and
  // time. Shared by both interceptors below.
  const addScannedUnverifiedVial = useCallback(
    (live, match, parsed, productId, { scanId, timestamp }) => {
      live.addVialManually(
        {
          drugName: match.drugName || parsed.drugName || '',
          manufacturer: parsed.manufacturer || '',
          strength: match.strength || parsed.strength || '',
          ndc: match.ndc,
          lotNumber: match.lotNumber,
          productId,
          quantity: parsed.quantity,
          inventoryLotId: match.inventoryLotId,
          expirationDate: budOf(match),
          suppliesBud: false,
          isNewLot: false,
          // Scanned, not typed — see the row chip in CurrentSession.
          manualEntry: false,
          matchedFromScan: true,
        },
        { scanId, timestamp }
      );
      playSuccessBeep();
      notify(
        `Lot ${match.lotNumber} has no stock but is on record in inventory — added as a ${SYSTEM_DEFICIT.toLowerCase()} lot.`,
        'info'
      );
    },
    [notify]
  );

  // A vial that is not on this pharmacy's shelf at all — stocked only at
  // another site, or unknown. With the manual-lot switch on, the unverified-lot
  // route is offered; otherwise the read is refused here with the reason,
  // rather than accepted and refused at commit with a generic error.
  const rejectNotAtFacility = useCallback(
    ({ lotNumber, ndc }) => {
      if (AddManualLotEnabled) {
        offerManualEntry({
          lotNumber,
          ndc,
          reason: `Lot ${lotNumber} is not in stock at this pharmacy`,
        });
        return;
      }
      playWarningBeep();
      notify(
        `Lot ${lotNumber} is not in stock at this pharmacy — it cannot be dispensed from here.`,
        'error'
      );
    },
    [offerManualEntry, notify]
  );

  // A vial the scan app DID verify against its listing. That listing is GET
  // /inventory, which has NO pharmacy dimension and reports DEPLETED lots as
  // well (quantity 0, dropped on the way to the CSV) — so a lot stocked only at
  // another site, or drained here, verifies exactly like one on this shelf and,
  // added as an ordinary vial, is refused at commit (INVENTORY_LOT_NOT_FOUND).
  // This pharmacy's own lists are the tie-breaker:
  //   - in dispensable stock here      → ordinary vial (fall through);
  //   - depleted here                   → added from the scan as an unverified
  //                                       lot, no dialog — strictly, see
  //                                       matchScannedLotToInventory; a depleted
  //                                       lot with no BUD on record, or under
  //                                       several BUDs, needs the operator and
  //                                       is offered the dialog;
  //   - neither (elsewhere / unknown)   → rejectNotAtFacility.
  // Until the facility's stock has loaded (or if that fetch failed) the third
  // branch is skipped: "not in a list we do not have" proves nothing.
  const handleVerifiedVial = useCallback(
    (parsed, meta = {}) => {
      if (!session.current || !prescribedProductId || !fulfillingPharmacyId) return false;
      const lotNumber = typeof parsed?.lotNumber === 'string' ? parsed.lotNumber.trim() : '';
      const ndc = typeof parsed?.ndc === 'string' ? parsed.ndc.trim() : '';
      const sameLot = (lot) => normalizeLot(lot.lotNumber) === normalizeLot(lotNumber);

      const stockedHere = facility.availableLots.some(
        (lot) => sameLot(lot) && (!ndc || ndcsMatch(lot.ndc, ndc))
      );
      if (stockedHere) return false;

      const depleted = facility.depletedLots.filter(sameLot);
      if (depleted.length > 0) {
        if (!AddManualLotEnabled) return false;
        const match = matchScannedLotToInventory({
          rows: depleted.map((lot) => ({
            ...lot,
            productId: prescribedProductId,
            fulfillingPharmacyId,
          })),
          lotNumber,
          fulfillingPharmacyId,
          productId: prescribedProductId,
          ndc,
        });
        if (!match) {
          offerManualEntry({
            lotNumber,
            ndc,
            reason: `Lot ${lotNumber} has no stock and its BUD must be confirmed`,
          });
          return true;
        }
        addScannedUnverifiedVial(session, match, parsed, prescribedProductId, meta);
        return true;
      }

      if (!facility.availableLoaded) return false;
      rejectNotAtFacility({ lotNumber, ndc });
      return true;
    },
    [
      session,
      prescribedProductId,
      fulfillingPharmacyId,
      facility.availableLots,
      facility.availableLoaded,
      facility.depletedLots,
      offerManualEntry,
      addScannedUnverifiedVial,
      rejectNotAtFacility,
    ]
  );

  // A vial the scan app could NOT verify against its listing. "Not in the
  // listing" is usually "unknown to inventory", but the listing is polled
  // every 15 minutes and can lag a lot inventory already has on record — so
  // the read lot is looked up first, and when it resolves to one of this
  // pharmacy's rows for the Rx's product — strictly, see
  // matchScannedLotToInventory — the row's status decides, no dialog:
  //   - stocked  → ordinary vial (a receipt the listing has not caught up with);
  //   - expired  → refused: stock past its BUD cannot be dispensed, and must
  //                not be recorded by hand either;
  //   - depleted → added from the scan as an unverified lot.
  // Anything less certain (unknown lot, several BUDs, no BUD on record, NDC
  // disagreement, lookup failure) offers the manual dialog pre-filled from the
  // read.
  //
  // Handled only with a session open: without one nothing can be matched and
  // the dialog would open disabled. Returns true when the read is taken, so
  // wireScanEvent leaves feedback to us.
  const handleUnverifiedVial = useCallback(
    (parsed, meta = {}) => {
      if (!AddManualLotEnabled || !session.current) return false;
      const lotNumber = typeof parsed?.lotNumber === 'string' ? parsed.lotNumber.trim() : '';
      const ndc = typeof parsed?.ndc === 'string' ? parsed.ndc.trim() : '';
      const productId = prescribedProductId;
      const sessionId = session.current.id;

      if (!lotNumber) {
        offerManualEntry({
          lotNumber,
          ndc,
          reason: 'Could not verify this vial against inventory',
        });
        return true;
      }
      if (!productId || !fulfillingPharmacyId) {
        offerManualEntry({ lotNumber, ndc, reason: `Lot ${lotNumber} is not in inventory` });
        return true;
      }

      inventoryLotsByLotIdApi({ lotId: lotNumber })
        .then((rows) => {
          const match = matchScannedLotToInventory({
            rows,
            lotNumber,
            fulfillingPharmacyId,
            productId,
            ndc,
          });
          // The session may have been swapped while the lookup ran; never add
          // the vial to a different Rx than the one it was scanned under.
          const live = sessionRef.current;
          if (!match || live.current?.id !== sessionId) {
            offerManualEntry({ lotNumber, ndc, reason: `Lot ${lotNumber} is not in inventory` });
            return;
          }
          const status = lotRowStatus(match);
          if (status === 'stocked') {
            live.onVialScanned(
              {
                ...parsed,
                drugName: match.drugName || parsed.drugName,
                ndc: match.ndc,
                lotNumber: match.lotNumber,
                productId,
                unsure: false,
              },
              meta.scanId,
              meta.timestamp
            );
            playSuccessBeep();
            notify(`Lot ${match.lotNumber} is in stock at this pharmacy — added.`, 'info');
            return;
          }
          if (status === 'expired') {
            playWarningBeep();
            notify(
              `Lot ${match.lotNumber} is on this pharmacy's shelf but past its BUD — it cannot be dispensed.`,
              'error'
            );
            return;
          }
          addScannedUnverifiedVial(live, match, parsed, productId, meta);
        })
        .catch(() =>
          offerManualEntry({ lotNumber, ndc, reason: `Lot ${lotNumber} is not in inventory` })
        );
      return true;
    },
    [
      session,
      prescribedProductId,
      fulfillingPharmacyId,
      offerManualEntry,
      addScannedUnverifiedVial,
      notify,
    ]
  );

  const handleEvent = useCallback(
    (event) => {
      // Handled here rather than in wireScanEvent: it's a reply to our own
      // request (see the effect below), not a scan the session state cares about.
      if (event?.type === 'inventory:response') {
        const items = event.items ?? [];
        // Functional form: returning the SAME array reference when nothing
        // changed lets React bail out of the re-render entirely (it compares
        // with Object.is), rather than only skip work we'd do downstream.
        session.setInventory((prev) => (inventoryRowsEqual(prev, items) ? prev : items));
        return;
      }
      wireScanEvent(session, event, notify, {
        onUnverifiedVial: handleUnverifiedVial,
        onVerifiedVial: handleVerifiedVial,
      });
    },
    [session, notify, handleUnverifiedVial, handleVerifiedVial]
  );

  const handleError = useCallback((msg) => notify(msg, 'error'), [notify]);

  const { status, send } = useScanSocket(handleEvent, handleError);

  // Once the socket is (re)connected, ask the scan app for its current
  // inventory listing; the response is handled by handleEvent above.
  useEffect(() => {
    if (status === 'open') send({ type: 'inventory:request' });
  }, [status, send]);

  // A manually typed Rx number goes through the exact same pipeline as a
  // scanned bottle (duplicate/commit-block rules, POM validation, optimistic
  // swap) by synthesizing a scan:complete event.
  const handleManualRx = useCallback(
    (rxNumber) =>
      wireScanEvent(
        session,
        {
          type: 'scan:complete',
          scanId: manualScanId(),
          timestamp: new Date().toISOString(),
          parsed: { containerType: 'rx_bottle', rxNumber, manualEntry: true },
        },
        notify
      ),
    [session, notify]
  );

  const handleCommit = useCallback(() => commitSession(session, notify), [session, notify]);

  // A vial's QR code read by the handheld scanner. It carries only the lot
  // number, so the vial's drug / NDC / product are looked up in the scan app's
  // inventory listing (session.inventory) by lot — preferring the open Rx's
  // product when the same lot number appears under several — and the result
  // is fed through the SAME pipeline as a camera vial read:
  //   - in the listing     → a verified vial (handleVerifiedVial then checks it
  //                          against this pharmacy's stock);
  //   - not in the listing → an unverified read (handleUnverifiedVial looks the
  //                          lot up in inventory or offers the manual dialog).
  // A vial needs an Rx to be matched against, so one scanned first is refused.
  // A rescan of the same lot bumps the vial's quantity (see 'vial-scanned').
  const handleVialLotScan = useCallback(
    (code) => {
      if (!session.current) {
        playWarningBeep();
        notify('Scan the Rx label first, then scan the vial.', 'warning');
        return;
      }
      if (session.current.rx?.validating) {
        playWarningBeep();
        notify('The Rx is still validating — scan the vial again in a moment.', 'warning');
        return;
      }
      // The QR may omit the lot's dash: match the listing on letters and digits
      // only, and send exact-string lookups the lot with its dash restored.
      const lotNumber = canonicalVialLot(code);
      const key = vialLotKey(code);
      const listed = session.inventory.filter((row) => vialLotKey(row.lotNumber) === key);
      const row = listed.find((r) => r.productId === prescribedProductId) ?? listed[0];
      // With the manual-lot switch off the unverified path declines the read and
      // wireScanEvent's generic "re-scan" toast would follow — but re-scanning a
      // QR reads the same lot, so name the actual problem instead.
      if (!row && !AddManualLotEnabled) {
        playWarningBeep();
        notify(`Lot ${lotNumber} is not in the inventory.`, 'error');
        return;
      }
      const parsed = row
        ? {
            containerType: 'drug_vial',
            drugName: row.drugName,
            manufacturer: row.manufacturer,
            strength: row.strength,
            ndc: row.ndc,
            lotNumber: row.lotNumber,
            productId: row.productId,
          }
        : { containerType: 'drug_vial', lotNumber, unsure: true };
      wireScanEvent(
        session,
        {
          type: 'scan:complete',
          scanId: manualScanId(),
          timestamp: new Date().toISOString(),
          parsed,
        },
        notify,
        { onUnverifiedVial: handleUnverifiedVial, onVerifiedVial: handleVerifiedVial }
      );
    },
    [session, notify, prescribedProductId, handleUnverifiedVial, handleVerifiedVial]
  );

  // A physical handheld scanner (separate from the 4-camera rig, and its own
  // input channel — see useHardwareScanner) reads either a vial's lot QR code
  // (see isVialLotCode) or an Rx bottle's barcode. The latter goes through the
  // exact same pipeline as a manually typed Rx number: getRxByScanId resolves
  // it (see resolveRxRecord in rxScanValidation.js) and a new session is
  // started/swapped in.
  const handleHardwareScan = useCallback(
    (code) => {
      if (isVialLotCode(code)) {
        handleVialLotScan(code);
        return;
      }
      wireScanEvent(
        session,
        {
          type: 'scan:complete',
          scanId: manualScanId(),
          timestamp: new Date().toISOString(),
          parsed: { containerType: 'rx_bottle', barcode: code },
        },
        notify
      );
    },
    [session, notify, handleVialLotScan]
  );
  useHardwareScanner(handleHardwareScan);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='h5'>Rx Scan</Typography>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Button
            size='small'
            variant='outlined'
            startIcon={<KeyboardIcon />}
            onClick={() => setManualRxOpen(true)}
            disabled={status !== 'open'}
          >
            Enter Rx manually
          </Button>
          <ConnectionStatusPill status={status} />
        </Stack>
      </Stack>

      <PendingVialBanner
        vial={session.pendingVial}
        hasCurrent={!!session.current}
        onAttach={session.promotePendingVial}
        onDismiss={session.dismissPendingVial}
      />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <CurrentSession
            session={session.current}
            inventory={session.inventory}
            commitBlockReason={commitBlockReason(session.current, session.inventory)}
            onEditRx={session.editRx}
            onEditVial={session.editVial}
            onAddVial={() => openAddDrug()}
            onRemoveVial={session.removeVial}
            onCommit={handleCommit}
            onDiscard={session.discardCurrent}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SessionHistory sessions={session.history} />
        </Grid>
      </Grid>

      <ManualRxDialog
        open={manualRxOpen}
        onClose={() => setManualRxOpen(false)}
        onSubmit={handleManualRx}
      />

      <AddDrugByLotDialog
        open={addDrugOpen}
        onClose={() => {
          setAddDrugOpen(false);
          setAddDrugSeed(null);
        }}
        inventory={session.inventory}
        productId={prescribedProductId}
        rxNdc={session.current?.rx?.ndc ?? null}
        fulfillingPharmacyId={fulfillingPharmacyId}
        facility={facility}
        seed={addDrugSeed}
        onConfirm={session.addVialFromInventory}
        onConfirmManual={session.addVialManually}
      />
    </Box>
  );
};

const RxScanStation = () => (
  <ScanSessionProvider>
    <RxScanStationInner />
  </ScanSessionProvider>
);

export default RxScanStation;
